import { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import CircularProgress from '@mui/material/CircularProgress'
import { useRatingsStore } from '../../stores/ratingsStore'
import Database from '../../types/Database'

export interface RatingFormProps {
  diveId: string
  rateeId: string
  onSuccess?: () => void
}

interface RatingFormValues {
  showedUp: boolean
  divedResponsibly: boolean
  knewGearSetup: boolean
  goodBuddy: boolean
  wouldDiveAgain: boolean
  comment: string
}

const TICK_LABELS: { name: keyof Database.RatingTicks; label: string }[] = [
  { name: 'showedUp', label: 'Showed up' },
  { name: 'divedResponsibly', label: 'Dived responsibly' },
  { name: 'knewGearSetup', label: 'Knew how to set up gear' },
  { name: 'goodBuddy', label: 'Good buddy' },
  { name: 'wouldDiveAgain', label: 'Would dive again' }
]

const COMMENT_MAX_LENGTH = 500

const RatingForm = ({ diveId, rateeId, onSuccess }: RatingFormProps) => {
  const { submitRating, submitting, error } = useRatingsStore()
  const [showSuccess, setShowSuccess] = useState(false)

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors }
  } = useForm<RatingFormValues>({
    defaultValues: {
      showedUp: false,
      divedResponsibly: false,
      knewGearSetup: false,
      goodBuddy: false,
      wouldDiveAgain: false,
      comment: ''
    }
  })

  const commentValue = watch('comment')

  const onSubmit = async (data: RatingFormValues) => {
    const input: Database.SubmitRatingInput = {
      diveId,
      rateeId,
      ticks: {
        showedUp: data.showedUp,
        divedResponsibly: data.divedResponsibly,
        knewGearSetup: data.knewGearSetup,
        goodBuddy: data.goodBuddy,
        wouldDiveAgain: data.wouldDiveAgain
      },
      comment: data.comment.trim() || null
    }

    await submitRating(input)

    // Check if submission was successful (no error set by the store)
    const storeError = useRatingsStore.getState().error
    if (!storeError) {
      setShowSuccess(true)
      reset()
      onSuccess?.()
    }
  }

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)} noValidate>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
        {TICK_LABELS.map(({ name, label }) => (
          <Controller
            key={name}
            name={name}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value}
                    onChange={field.onChange}
                    disabled={submitting}
                  />
                }
                label={label}
              />
            )}
          />
        ))}
      </Box>

      <Controller
        name='comment'
        control={control}
        rules={{
          maxLength: {
            value: COMMENT_MAX_LENGTH,
            message: 'Comment must be 500 characters or fewer'
          }
        }}
        render={({ field }) => (
          <TextField
            {...field}
            label='Comment (optional)'
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            disabled={submitting}
            error={!!errors.comment}
            helperText={errors.comment?.message}
            sx={{ mb: 1 }}
          />
        )}
      />

      <Typography
        variant='caption'
        color={
          commentValue.length > COMMENT_MAX_LENGTH ? 'error' : 'text.secondary'
        }
        sx={{ mb: 2, display: 'block' }}
      >
        {commentValue.length}/{COMMENT_MAX_LENGTH}
      </Typography>

      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        type='submit'
        variant='contained'
        fullWidth
        disabled={submitting}
        startIcon={submitting ? <CircularProgress size={20} /> : undefined}
      >
        {submitting ? 'Submitting…' : 'Submit Rating'}
      </Button>

      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity='success' onClose={() => setShowSuccess(false)}>
          Rating submitted successfully!
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default RatingForm
